/**
 * WavesPanel — Deployment Waves management for the Planner.
 *
 * Lets users partition the estimate scope into deployment waves:
 *  - create/rename/describe/delete waves (with optional manual start & duration)
 *  - allocate each estimate component fully to one wave, or split it by %
 *  - per-estimate toggle: each wave gets its own mini-lifecycle
 *  - per-wave summary cards: effort, window, Seertech roles, client load
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Layers, Plus, Trash2, Save, AlertTriangle, SplitSquareHorizontal,
  CalendarRange, Users, Loader2, BookmarkPlus, FolderDown, X, Scale, Receipt, FastForward, SlidersHorizontal, CopyPlus,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WAVE_COLORS = ['#4ABFCF', '#8B5CF6', '#F59E0B', '#10B981', '#EF4444', '#3B82F6'];

// v261 — Mini strip of wave positions used by the mirror preview dialog.
function WaveStrip({ list, span, label, templateId, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      <div className="space-y-1">
        {list.map((w, i) => {
          const dated = w.start_week && w.duration_weeks;
          const isTpl = w.id === templateId;
          return (
            <div key={w.id} className="flex items-center gap-2">
              <span className={`w-24 truncate text-[10px] ${isTpl ? 'font-semibold text-[#0F2040]' : 'text-slate-600'}`}>
                {w.name}{isTpl ? ' ★' : ''}
              </span>
              <div className="relative flex-1 h-4 bg-slate-100 rounded overflow-hidden">
                {dated ? (
                  <div
                    className={`absolute top-0 h-4 rounded text-[9px] text-white flex items-center justify-center whitespace-nowrap px-1 ${isTpl ? 'ring-2 ring-[#0F2040]/40' : ''}`}
                    style={{
                      left: `${((w.start_week - 1) / span) * 100}%`,
                      width: `${Math.max((w.duration_weeks / span) * 100, 6)}%`,
                      background: WAVE_COLORS[i % WAVE_COLORS.length],
                    }}
                  >
                    W{w.start_week}–W{w.start_week + w.duration_weeks - 1}
                  </div>
                ) : (
                  <span className="text-[9px] text-slate-400 pl-1.5 leading-4">auto layout</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const STATUS_META = {
  planned: { label: 'Planned', cls: 'bg-slate-100 text-slate-600 border-slate-300' },
  in_flight: { label: 'In flight', cls: 'bg-sky-100 text-sky-700 border-sky-300' },
  launched: { label: 'Launched', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

function fmtCost(v, sym = '$') {
  if (v == null) return null;
  return `${sym}${Math.round(v).toLocaleString()}`;
}

function slipMeta(slipDays) {
  if (slipDays == null) return null;
  if (slipDays === 0) return { label: 'On time', cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' };
  if (slipDays > 0) return { label: `${slipDays}d late`, cls: 'bg-rose-50 text-rose-700 border-rose-300' };
  return { label: `${Math.abs(slipDays)}d early`, cls: 'bg-emerald-50 text-emerald-700 border-emerald-300' };
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

export function WavesPanel({ estimateId, timeline, onSaved, readOnly = false, currencySymbol = '$' }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [splitMode, setSplitMode] = useState({}); // {componentName: true}
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateNameInput, setTemplateNameInput] = useState(null); // null = closed
  const [mirrorOverlap, setMirrorOverlap] = useState(0); // wks of overlap between mirrored waves (negative = gap)
  const [mirrorSourceId, setMirrorSourceId] = useState(''); // '' = first wave
  const [mirrorPreview, setMirrorPreview] = useState(null); // computed mirror result awaiting confirmation
  const [mirrorTargets, setMirrorTargets] = useState(new Set()); // wave ids the template applies to
  const [templateSaving, setTemplateSaving] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [compareTplB, setCompareTplB] = useState(null);
  const [compareTplC, setCompareTplC] = useState(null);
  const [replanning, setReplanning] = useState(false);
  const [timingOpen, setTimingOpen] = useState({}); // {waveId: bool}

  const setTiming = useCallback((compName, waveId, patch) => {
    setCfg(prev => {
      const ct = { ...(prev.component_timing || {}) };
      const perWave = { ...(ct[compName] || {}) };
      if (patch === null) delete perWave[waveId];
      else perWave[waveId] = { ...(perWave[waveId] || {}), ...patch };
      if (Object.keys(perWave).length) ct[compName] = perWave; else delete ct[compName];
      return { ...prev, component_timing: ct };
    });
    setDirty(true);
  }, []);

  const loadTemplates = useCallback(() => {
    axios.get(`${API_URL}/api/planner/wave-templates`, { withCredentials: true })
      .then(res => setTemplates(res.data?.templates || []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  useEffect(() => {
    if (!estimateId) return;
    setCfg(null);
    axios.get(`${API_URL}/api/planner/waves/${estimateId}`, { withCredentials: true })
      .then(res => {
        const c = res.data || {};
        setCfg({
          enabled: !!c.enabled,
          per_wave_lifecycle: !!c.per_wave_lifecycle,
          waves: c.waves || [],
          allocations: c.allocations || {},
          component_timing: c.component_timing || {},
          last_replan: c.last_replan || null,
        });
        // Pre-open split mode for components already fractionally allocated
        const sm = {};
        Object.entries(c.allocations || {}).forEach(([name, fm]) => {
          const vals = Object.values(fm || {});
          if (vals.length > 1 || (vals.length === 1 && vals[0] < 0.999)) sm[name] = true;
        });
        setSplitMode(sm);
        setDirty(false);
      })
      .catch(() => toast.error('Could not load waves configuration'));
  }, [estimateId]);

  // Flatten timeline components (name, group, effort)
  const components = useMemo(() => {
    const out = [];
    (timeline?.group_schedule || []).forEach(g => {
      (g.components || []).forEach(c => {
        if ((c.effort || 0) > 0) out.push({ name: c.name, group: g.group, effort: c.effort });
      });
    });
    return out;
  }, [timeline]);

  const update = useCallback((patch) => {
    if (readOnly) return; // view-only role — panel is inert
    setCfg(prev => ({ ...prev, ...patch }));
    setDirty(true);
  }, [readOnly]);

  const addWave = () => {
    const n = (cfg.waves?.length || 0) + 1;
    update({
      waves: [...(cfg.waves || []), {
        id: `wave-${Date.now()}`,
        name: `Wave ${n}`,
        description: '',
        sequence: n,
        start_week: null,
        duration_weeks: null,
      }],
    });
  };

  const removeWave = (wid) => {
    const allocations = {};
    Object.entries(cfg.allocations || {}).forEach(([name, fm]) => {
      const clean = { ...fm };
      delete clean[wid];
      if (Object.keys(clean).length) allocations[name] = clean;
    });
    update({
      waves: (cfg.waves || []).filter(w => w.id !== wid).map((w, i) => ({ ...w, sequence: i + 1 })),
      allocations,
    });
  };

  const patchWave = (wid, patch) => {
    update({ waves: (cfg.waves || []).map(w => (w.id === wid ? { ...w, ...patch } : w)) });
  };

  // v261 — Mirror a wave: apply any chosen wave's configuration to every
  // other wave as a template. Two dimensions:
  //   1. Wave level — when the template wave has an explicit start +
  //      duration, other waves take the same duration and chain around it
  //      in sequence order, staggered by the overlap (negative = gap).
  //   2. Task level — each component allocated in both the template wave
  //      and another wave copies its relative start (+N wks) and duration;
  //      if the component isn't fine-tuned in the template, the other
  //      wave's tuning is cleared so the layout truly mirrors.
  // Changes stay local until "Save waves" is clicked.
  const computeMirror = (targetIds = null) => {
    const ordered = [...(cfg.waves || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    if (ordered.length < 2) return null;
    const srcIdx = Math.max(0, ordered.findIndex(w => w.id === mirrorSourceId));
    const src = ordered[srcIdx];
    // targetIds: waves the template applies to (null = all other waves)
    const others = ordered.filter(w => w.id !== src.id && (!targetIds || targetIds.has(w.id)));
    const otherIds = new Set(others.map(w => w.id));

    let timingCopies = 0;
    let timingClears = 0;
    const ct = { ...(cfg.component_timing || {}) };
    Object.entries(cfg.allocations || {}).forEach(([comp, fm]) => {
      if (!fm || fm[src.id] == null) return; // template wave carries no pattern for this component
      const pattern = (ct[comp] || {})[src.id] || null;
      others.forEach(w => {
        if (fm[w.id] == null) return; // component not allocated to this wave
        const perWave = { ...(ct[comp] || {}) };
        if (pattern) {
          perWave[w.id] = { ...pattern };
          timingCopies += 1;
        } else if (perWave[w.id]) {
          delete perWave[w.id];
          timingClears += 1;
        }
        if (Object.keys(perWave).length) ct[comp] = perWave; else delete ct[comp];
      });
    });

    let newWaves = cfg.waves || [];
    let chained = false;
    let step = 0;
    if (src.start_week && src.duration_weeks) {
      chained = true;
      // Overlap shortens the gap between successive starts; earlier waves
      // chain backwards from the template, later waves forwards, and no
      // wave can start before week 1.
      step = Math.max(1, src.duration_weeks - mirrorOverlap);
      const startById = {};
      ordered.forEach((w, j) => {
        if (!otherIds.has(w.id)) return;
        startById[w.id] = Math.max(1, src.start_week + (j - srcIdx) * step);
      });
      newWaves = newWaves.map(w => (otherIds.has(w.id)
        ? { ...w, start_week: startById[w.id], duration_weeks: src.duration_weeks }
        : w));
    }

    const bits = [];
    if (chained) {
      const overlapTxt = mirrorOverlap > 0
        ? `, overlapping ${Math.min(mirrorOverlap, src.duration_weeks - 1)} wk${mirrorOverlap === 1 ? '' : 's'}`
        : (mirrorOverlap < 0 ? `, ${Math.abs(mirrorOverlap)} wk${mirrorOverlap === -1 ? '' : 's'} gap` : '');
      bits.push(`durations set to ${src.duration_weeks} wks, starts every ${step} wks around ${src.name}${overlapTxt}`);
    }
    if (timingCopies) bits.push(`${timingCopies} task timing${timingCopies === 1 ? '' : 's'} copied`);
    if (timingClears) bits.push(`${timingClears} cleared to match`);

    const before = ordered;
    const after = [...newWaves].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    return { src, others, newWaves, ct, chained, bits, before, after, candidates: ordered.filter(w => w.id !== src.id) };
  };

  const openMirrorPreview = () => {
    const r = computeMirror();
    if (r) {
      setMirrorTargets(new Set(r.candidates.map(w => w.id)));
      setMirrorPreview(r);
    }
  };

  const toggleMirrorTarget = (wid) => {
    const next = new Set(mirrorTargets);
    if (next.has(wid)) next.delete(wid); else next.add(wid);
    setMirrorTargets(next);
    setMirrorPreview(computeMirror(next));
  };

  const applyMirror = () => {
    const r = mirrorPreview;
    if (!r) return;
    update({ waves: r.newWaves, component_timing: r.ct });
    setMirrorPreview(null);
    toast.success(
      `${r.src.name} mirrored to ${r.others.length} wave${r.others.length === 1 ? '' : 's'}${r.bits.length ? ` — ${r.bits.join(', ')}` : ''}. Review, then Save waves.`,
      { duration: 7000 }
    );
  };

  // Whole-component assignment via select
  const assignWhole = (compName, wid) => {
    const allocations = { ...(cfg.allocations || {}) };
    if (!wid) delete allocations[compName];
    else allocations[compName] = { [wid]: 1.0 };
    setSplitMode(prev => ({ ...prev, [compName]: false }));
    update({ allocations });
  };

  const setFraction = (compName, wid, pct) => {
    const allocations = { ...(cfg.allocations || {}) };
    const fm = { ...(allocations[compName] || {}) };
    const f = Math.max(0, Math.min(100, parseFloat(pct) || 0)) / 100;
    if (f <= 0) delete fm[wid]; else fm[wid] = f;
    if (Object.keys(fm).length) allocations[compName] = fm; else delete allocations[compName];
    update({ allocations });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/planner/waves/${estimateId}`, cfg, { withCredentials: true });
      toast.success('Deployment waves saved');
      setDirty(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save waves');
    } finally {
      setSaving(false);
    }
  };

  const saveAsTemplate = async () => {
    const name = (templateNameInput || '').trim();
    if (!name) { toast.error('Give the template a name'); return; }
    setTemplateSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/planner/wave-templates`, {
        name,
        per_wave_lifecycle: cfg.per_wave_lifecycle,
        waves: cfg.waves,
        allocations: cfg.allocations,
        component_timing: cfg.component_timing || {},
      }, { withCredentials: true });
      toast.success(`Template "${name}" saved`);
      setTemplateNameInput(null);
      const newId = res.data?.template?.id;
      if (newId) setSelectedTemplateId(newId);
      loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save template');
    } finally {
      setTemplateSaving(false);
    }
  };

  const applyTemplateObj = (tpl) => {
    if (!tpl) return;
    const idMap = {};
    const now = Date.now();
    const newWaves = (tpl.waves || []).map((w, i) => {
      const nid = `wave-${now}-${i}`;
      idMap[w.id] = nid;
      return { ...w, id: nid };
    });
    const compNames = new Set(components.map(c => c.name));
    const newAlloc = {};
    let skipped = 0;
    Object.entries(tpl.allocations || {}).forEach(([name, fm]) => {
      if (!compNames.has(name)) { skipped += 1; return; }
      const mapped = {};
      Object.entries(fm || {}).forEach(([wid, f]) => { if (idMap[wid]) mapped[idMap[wid]] = f; });
      if (Object.keys(mapped).length) newAlloc[name] = mapped;
    });
    const newTiming = {};
    Object.entries(tpl.component_timing || {}).forEach(([name, perWave]) => {
      if (!compNames.has(name)) return;
      const mapped = {};
      Object.entries(perWave || {}).forEach(([wid, t]) => { if (idMap[wid]) mapped[idMap[wid]] = t; });
      if (Object.keys(mapped).length) newTiming[name] = mapped;
    });
    const sm = {};
    Object.entries(newAlloc).forEach(([name, fm]) => {
      const vals = Object.values(fm);
      if (vals.length > 1 || (vals.length === 1 && vals[0] < 0.999)) sm[name] = true;
    });
    setSplitMode(sm);
    update({ enabled: true, per_wave_lifecycle: !!tpl.per_wave_lifecycle, waves: newWaves, allocations: newAlloc, component_timing: newTiming });
    toast.success(
      `Template "${tpl.name}" applied${skipped ? ` — ${skipped} allocation(s) skipped (component not in this estimate)` : ''} — review and Save waves`,
    );
  };

  const applyTemplate = () => applyTemplateObj(templates.find(t => t.id === selectedTemplateId));

  const deleteTemplate = async () => {
    const tpl = templates.find(t => t.id === selectedTemplateId);
    if (!tpl) return;
    try {
      await axios.delete(`${API_URL}/api/planner/wave-templates/${tpl.id}`, { withCredentials: true });
      toast.success('Template deleted');
      setSelectedTemplateId('');
      loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not delete template');
    }
  };

  const tplToConfig = (tpl) => ({
    enabled: true,
    per_wave_lifecycle: !!tpl.per_wave_lifecycle,
    waves: tpl.waves,
    allocations: tpl.allocations,
    component_timing: tpl.component_timing || {},
  });

  const runCompare = async (tplB, tplC) => {
    setComparing(true);
    try {
      const payload = {
        config_a: cfg,
        config_b: tplToConfig(tplB),
        label_a: 'Current layout',
        label_b: tplB.name,
      };
      if (tplC) {
        payload.config_c = tplToConfig(tplC);
        payload.label_c = tplC.name;
      }
      const res = await axios.post(`${API_URL}/api/planner/waves/${estimateId}/compare`, payload, { withCredentials: true });
      setCompareData(res.data);
      setCompareOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not compare layouts');
    } finally {
      setComparing(false);
    }
  };

  const compareWithTemplate = () => {
    const tpl = templates.find(t => t.id === selectedTemplateId);
    if (!tpl) return;
    setCompareTplB(tpl);
    setCompareTplC(null);
    runCompare(tpl, null);
  };

  if (!cfg) {
    return (
      <Card><CardContent className="py-10 flex items-center justify-center text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading waves…
      </CardContent></Card>
    );
  }

  const waves = cfg.waves || [];
  const waveSchedule = timeline?.wave_schedule || [];
  const wavesMeta = timeline?.waves_meta || null;

  const slippedLaunched = waveSchedule.filter(ws => ws.status === 'launched' && (ws.slip_days || 0) > 0);
  const replanAnchor = slippedLaunched.length
    ? slippedLaunched.reduce((a, b) => ((a.slip_days || 0) >= (b.slip_days || 0) ? a : b))
    : null;
  const replanDownstream = replanAnchor
    ? waveSchedule.filter(ws => ws.sequence > replanAnchor.sequence && ws.status !== 'launched')
    : [];
  const alreadyReplanned = replanAnchor && cfg.last_replan
    && cfg.last_replan.anchor_wave_id === replanAnchor.wave_id
    && cfg.last_replan.slip_days === replanAnchor.slip_days;
  const replanCandidate = replanAnchor && replanDownstream.length && !alreadyReplanned
    ? { anchor: replanAnchor, downstream: replanDownstream, slipWeeks: Math.ceil((replanAnchor.slip_days || 0) / 7) }
    : null;

  const replan = async () => {
    setReplanning(true);
    try {
      const res = await axios.post(`${API_URL}/api/planner/waves/${estimateId}/replan`,
        { start_date: timeline?.project_start_date || timeline?.goal_start_date || null },
        { withCredentials: true });
      toast.success(res.data?.message || 'Downstream waves shifted');
      const c = res.data?.config;
      if (c) {
        setCfg({ enabled: !!c.enabled, per_wave_lifecycle: !!c.per_wave_lifecycle, waves: c.waves || [], allocations: c.allocations || {}, component_timing: c.component_timing || {}, last_replan: c.last_replan || null });
        setDirty(false);
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not replan waves');
    } finally {
      setReplanning(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="waves-panel">
      {/* ── Header / settings card ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
              <Layers className="w-4 h-4 text-[#4ABFCF]" /> Deployment Waves
            </CardTitle>
            <div className="flex items-center gap-4">
              {readOnly ? (
                <span className="text-[11px] text-slate-400 italic" data-testid="waves-readonly-note">View only</span>
              ) : (
                <>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch checked={cfg.enabled} onCheckedChange={(v) => update({ enabled: v })} data-testid="waves-enabled-switch" />
                Enable waves
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Switch checked={cfg.per_wave_lifecycle} onCheckedChange={(v) => update({ per_wave_lifecycle: v })} disabled={!cfg.enabled} data-testid="waves-lifecycle-switch" />
                Mini-lifecycle per wave
              </label>
              <Button size="sm" onClick={save} disabled={saving || !dirty} className="bg-[#0F2040] hover:bg-[#1a3057] text-white h-8" data-testid="waves-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save waves
              </Button>
                </>
              )}
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
            Split a single implementation scope into deployment waves. Allocate each component fully to one wave or split it by percentage — waves appear as sub-sections of the plan with their own timeline, Seertech role effort and client resource load.
            {cfg.per_wave_lifecycle && ' Each wave runs its own Design → Build → UAT → Go-Live lifecycle.'}
          </p>
        </CardHeader>
        {cfg.enabled && (
          <CardContent className="pt-0 space-y-3">
            {waveSchedule.some(ws => ws.status === 'launched' && ws.actual_golive_date && ws.slip_days == null) && (
              <div className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 text-[11px] text-slate-500" data-testid="slip-needs-dates-hint">
                <CalendarRange className="w-3.5 h-3.5 shrink-0 text-[#4ABFCF]" />
                A wave has a recorded go-live date, but slip can't be measured without calendar dates — generate the timeline in Goal Dates mode (or set a start date in Project Conditions) to see planned-vs-actual slip.
              </div>
            )}
            {replanCandidate && (
              <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 flex-wrap" data-testid="replan-callout">
                <span className="text-[11px] text-amber-800 flex-1 min-w-[240px]">
                  <strong>{replanCandidate.anchor.wave}</strong> went live {replanCandidate.anchor.slip_days} day{replanCandidate.anchor.slip_days === 1 ? '' : 's'} late — {replanCandidate.downstream.length} downstream wave{replanCandidate.downstream.length === 1 ? '' : 's'} ({replanCandidate.downstream.map(w => w.wave).join(', ')}) still reflect the original plan.
                </span>
                <Button size="sm" onClick={replan} disabled={replanning}
                  className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white shrink-0" data-testid="replan-btn">
                  {replanning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <FastForward className="w-3 h-3 mr-1" />}
                  Shift downstream +{replanCandidate.slipWeeks}w
                </Button>
              </div>
            )}
            {waves.map((w, i) => {
              const totalWeeks = timeline?.total_duration_weeks || 0;
              let overrun = 0;
              if (totalWeeks && w.start_week && w.duration_weeks) {
                overrun = Math.max(0, w.start_week + w.duration_weeks - 1 - totalWeeks);
              } else {
                const ws = waveSchedule.find(s => s.wave_id === w.id);
                overrun = ws?.overrun_weeks || 0;
              }
              return (
              <div key={w.id} className="border border-slate-200 rounded-md p-3 bg-slate-50/50" data-testid={`wave-config-${i}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: WAVE_COLORS[i % WAVE_COLORS.length] }} />
                  <Input value={w.name} onChange={(e) => patchWave(w.id, { name: e.target.value })}
                    className="h-8 text-sm font-medium w-40" data-testid={`wave-name-input-${i}`} />
                  <Input value={w.description || ''} placeholder="Description (optional)"
                    onChange={(e) => patchWave(w.id, { description: e.target.value })}
                    className="h-8 text-xs flex-1 min-w-[160px]" data-testid={`wave-desc-input-${i}`} />
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500">
                    Start W
                    <Input type="number" min="1" value={w.start_week ?? ''} placeholder="auto"
                      onChange={(e) => patchWave(w.id, { start_week: e.target.value ? parseInt(e.target.value, 10) : null })}
                      className="h-8 w-16 text-xs" data-testid={`wave-start-input-${i}`} />
                    Weeks
                    <Input type="number" min="1" value={w.duration_weeks ?? ''} placeholder="auto"
                      onChange={(e) => patchWave(w.id, { duration_weeks: e.target.value ? parseInt(e.target.value, 10) : null })}
                      className="h-8 w-16 text-xs" data-testid={`wave-duration-input-${i}`} />
                  </div>
                  <div className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-md p-0.5" data-testid={`wave-status-toggle-${i}`}>
                    {Object.entries(STATUS_META).map(([k, s]) => (
                      <button key={k} type="button"
                        onClick={() => patchWave(w.id, {
                          status: k,
                          ...(k === 'launched' && !w.actual_golive_date
                            ? { actual_golive_date: new Date().toISOString().slice(0, 10) }
                            : {}),
                        })}
                        className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors ${(w.status || 'planned') === k ? `${s.cls} font-semibold` : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                        data-testid={`wave-status-${i}-${k}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                  {(w.status === 'launched') && (
                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid={`wave-golive-date-wrap-${i}`}>
                      Went live
                      <Input type="date" value={w.actual_golive_date || ''}
                        onChange={(e) => patchWave(w.id, { actual_golive_date: e.target.value || null })}
                        className="h-8 w-36 text-xs" data-testid={`wave-golive-date-input-${i}`} />
                      {(() => {
                        const ws = waveSchedule.find(s => s.wave_id === w.id);
                        const sm = slipMeta(ws?.slip_days);
                        return sm ? (
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${sm.cls}`} data-testid={`wave-slip-chip-${i}`}>{sm.label}</span>
                        ) : null;
                      })()}
                    </div>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => removeWave(w.id)}
                    className="h-8 w-8 p-0 text-slate-400 hover:text-rose-600" data-testid={`wave-delete-btn-${i}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {overrun > 0 && (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700" data-testid={`wave-overrun-warning-${i}`}>
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span>
                      This wave runs <strong>{overrun} week{overrun === 1 ? '' : 's'} past the plan horizon</strong> (W{totalWeeks || timeline?.total_duration_weeks}). Shorten it, move it earlier, or extend the overall plan.
                    </span>
                  </div>
                )}
                {(() => {
                  const allocated = Object.entries(cfg.allocations || {})
                    .filter(([, fm]) => (fm?.[w.id] || 0) > 0)
                    .map(([n]) => n);
                  const tunedCount = allocated.filter(n => cfg.component_timing?.[n]?.[w.id]).length;
                  const ws = waveSchedule.find(s => s.wave_id === w.id);
                  const rw = (v) => Math.round(v * 10) / 10;
                  return (
                    <>
                      <button type="button"
                        onClick={() => setTimingOpen(p => ({ ...p, [w.id]: !p[w.id] }))}
                        className="mt-2 text-[10px] text-[#0F6B78] flex items-center gap-1 hover:underline"
                        data-testid={`wave-timing-toggle-${i}`}>
                        <SlidersHorizontal className="w-3 h-3" />
                        Fine-tune timing ({allocated.length} component{allocated.length === 1 ? '' : 's'}{tunedCount ? `, ${tunedCount} tuned` : ''})
                      </button>
                      {timingOpen[w.id] && (
                        <div className="mt-2 space-y-1.5 border-t border-slate-200 pt-2" data-testid={`wave-timing-panel-${i}`}>
                          {allocated.length === 0 && (
                            <p className="text-[10px] text-slate-400">Allocate components to this wave first.</p>
                          )}
                          {allocated.map((compName, ci) => {
                            const t = cfg.component_timing?.[compName]?.[w.id] || {};
                            const offVal = t.offset_weeks ?? '';
                            const durVal = t.duration_weeks ?? '';
                            const off = parseFloat(offVal) || 0;
                            const waveDur = ws?.duration_weeks;
                            const effDur = durVal !== '' ? parseFloat(durVal) : (waveDur != null ? Math.max(0.5, rw(waveDur - off)) : null);
                            const tOverrun = waveDur != null && effDur != null && off + effDur > waveDur + 1e-9;
                            const winTxt = ws?.start_week != null && effDur != null
                              ? `W${rw(ws.start_week + off)} – W${rw(ws.start_week + off + effDur - 1)}`
                              : null;
                            return (
                              <div key={compName} className="flex items-center gap-2 flex-wrap text-[11px]" data-testid={`timing-row-${i}-${ci}`}>
                                <span className="text-slate-600 font-medium min-w-[140px] flex-1 truncate">{compName}</span>
                                <label className="flex items-center gap-1 text-slate-500">
                                  Starts +
                                  <Input type="number" step="0.5" min="0" value={offVal} placeholder="0"
                                    onChange={(e) => setTiming(compName, w.id, { offset_weeks: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                                    className="h-7 w-16 text-xs" data-testid={`timing-offset-input-${i}-${ci}`} />
                                  wks
                                </label>
                                <label className="flex items-center gap-1 text-slate-500">
                                  Runs
                                  <Input type="number" step="0.5" min="0.1" value={durVal} placeholder="full wave"
                                    onChange={(e) => setTiming(compName, w.id, { duration_weeks: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                    className="h-7 w-20 text-xs" data-testid={`timing-duration-input-${i}-${ci}`} />
                                  wks
                                </label>
                                {winTxt && <span className="text-[10px] text-slate-400" data-testid={`timing-window-${i}-${ci}`}>{winTxt}</span>}
                                {tOverrun && (
                                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-300" data-testid={`timing-overrun-chip-${i}-${ci}`}>
                                    <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" />runs past wave end
                                  </span>
                                )}
                                {(t.offset_weeks != null || t.duration_weeks != null) && (
                                  <button type="button" onClick={() => setTiming(compName, w.id, null)}
                                    className="text-slate-300 hover:text-rose-500" title="Reset to full wave window"
                                    data-testid={`timing-reset-btn-${i}-${ci}`}>
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            );
                          })}
                          <p className="text-[9px] text-slate-400">Fractional weeks allowed (2.5 = 2 wks 3.5 days). Start offsets are relative to the wave start, so they follow the wave if it moves. Leave "Runs" blank to fill the wave.</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              );
            })}
            <div className="flex items-center gap-2 flex-wrap">
              {!readOnly && (
              <Button variant="outline" size="sm" onClick={addWave} className="h-8 text-xs" data-testid="wave-add-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add wave
              </Button>
              )}
              {!readOnly && waves.length >= 2 && (
                <>
                  <span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500">
                    <CopyPlus className="w-3.5 h-3.5 text-[#4ABFCF]" />
                    Mirror
                    <select
                      value={mirrorSourceId || waves[0]?.id || ''}
                      onChange={(e) => setMirrorSourceId(e.target.value)}
                      className="h-8 text-xs border border-slate-200 rounded px-1.5 bg-white text-slate-700"
                      title="Choose which wave acts as the template"
                      data-testid="wave-mirror-source-select"
                    >
                      {waves.map(w => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                      ))}
                    </select>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={openMirrorPreview}
                    className="h-8 text-xs border-[#4ABFCF]/50 text-[#0F2040] hover:bg-[#4ABFCF]/10"
                    title="Use the selected wave as the template: other waves copy its duration and chain around it in order (when it has explicit start + weeks), staggered by the overlap; each shared component copies its relative start and duration"
                    data-testid="wave-mirror-first-btn"
                  >
                    Apply to other waves
                  </Button>
                  <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                    overlap
                    <Input
                      type="number"
                      step="1"
                      value={mirrorOverlap}
                      onChange={(e) => setMirrorOverlap(e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0)}
                      className="h-8 w-14 text-xs"
                      title="Weeks each mirrored wave overlaps the previous one (0 = back-to-back, negative = gap between waves)"
                      data-testid="wave-mirror-overlap-input"
                    />
                    wks
                  </span>
                </>
              )}
            </div>
            {waves.length === 0 && (
              <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="waves-empty-hint">
                Waves are enabled but none exist yet — add at least one wave, then allocate components to it below and save.
              </div>
            )}
            <p className="text-[10px] text-slate-400">
              Leave Start/Weeks blank for automatic sequential layout (duration proportional to allocated effort). Set them manually to overlap waves.
            </p>
            {/* ── Templates ── */}
            <div className="flex items-center gap-2 flex-wrap pt-2.5 border-t border-slate-100" data-testid="wave-templates-row">
              <FolderDown className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="h-7 text-xs border border-slate-200 rounded px-2 bg-white text-slate-700 min-w-[180px]"
                data-testid="wave-template-select"
              >
                <option value="">Choose a template…</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name} ({(t.waves || []).length} waves)</option>
                ))}
              </select>
              <Button variant="outline" size="sm" onClick={applyTemplate} disabled={!selectedTemplateId}
                className="h-7 text-xs" data-testid="wave-template-apply-btn">
                Apply template
              </Button>
              <Button variant="outline" size="sm" onClick={compareWithTemplate} disabled={!selectedTemplateId || comparing || waves.length === 0}
                className="h-7 text-xs" data-testid="wave-template-compare-btn">
                {comparing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Scale className="w-3 h-3 mr-1" />} Compare
              </Button>
              {selectedTemplateId && (
                <Button variant="ghost" size="sm" onClick={deleteTemplate}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600" data-testid="wave-template-delete-btn">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
              <span className="text-slate-200">|</span>
              {templateNameInput === null ? (
                <Button variant="ghost" size="sm" onClick={() => setTemplateNameInput('')}
                  disabled={waves.length === 0}
                  className="h-7 text-xs text-[#0F6B78] hover:text-[#0F2040]" data-testid="wave-template-save-toggle">
                  <BookmarkPlus className="w-3.5 h-3.5 mr-1" /> Save as template
                </Button>
              ) : (
                <div className="flex items-center gap-1.5">
                  <Input value={templateNameInput} placeholder="Template name"
                    onChange={(e) => setTemplateNameInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveAsTemplate()}
                    className="h-7 w-44 text-xs" autoFocus data-testid="wave-template-name-input" />
                  <Button size="sm" onClick={saveAsTemplate} disabled={templateSaving}
                    className="h-7 text-xs bg-[#0F2040] hover:bg-[#1a3057] text-white" data-testid="wave-template-save-confirm">
                    {templateSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setTemplateNameInput(null)}
                    className="h-7 w-7 p-0 text-slate-400" data-testid="wave-template-save-cancel">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}
            </div>
            {selectedTemplateId && (() => {
              const tpl = templates.find(t => t.id === selectedTemplateId);
              if (!tpl) return null;
              const when = tpl.created_at ? new Date(tpl.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null;
              return (
                <p className="text-[10px] text-slate-400 -mt-1" data-testid="wave-template-meta">
                  Created by <span className="font-medium text-slate-500">{tpl.created_by_name || tpl.created_by || 'unknown'}</span>{when ? ` on ${when}` : ''} · {(tpl.waves || []).length} waves · {Object.keys(tpl.allocations || {}).length} allocation{Object.keys(tpl.allocations || {}).length === 1 ? '' : 's'}{tpl.per_wave_lifecycle ? ' · mini-lifecycle' : ''}
                </p>
              );
            })()}
          </CardContent>
        )}
      </Card>

      {/* ── Allocation matrix ── */}
      {cfg.enabled && waves.length > 0 && (
        <Card data-testid="waves-allocation-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040]">Component Allocation</CardTitle>
            <p className="text-[11px] text-slate-500">Assign each component to a wave, or use Split to divide it by percentage across waves.</p>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1">
              {components.map((comp, ci) => {
                const fm = cfg.allocations?.[comp.name] || {};
                const totalPct = Math.round(Object.values(fm).reduce((s, v) => s + (v || 0), 0) * 100);
                const isSplit = !!splitMode[comp.name];
                const wholeWave = !isSplit && Object.keys(fm).length === 1 && Math.abs(Object.values(fm)[0] - 1.0) < 0.001
                  ? Object.keys(fm)[0] : '';
                const over = totalPct > 100;
                return (
                  <div key={comp.name} className={`flex items-center gap-2 flex-wrap px-2 py-1.5 rounded border ${over ? 'border-rose-300 bg-rose-50/50' : 'border-slate-100 bg-white'}`} data-testid={`alloc-row-${ci}`}>
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-xs font-medium text-[#0F2040]">{comp.name}</div>
                      <div className="text-[10px] text-slate-400">{comp.group} · {comp.effort}h</div>
                    </div>
                    {!isSplit ? (
                      <select
                        value={wholeWave}
                        onChange={(e) => assignWhole(comp.name, e.target.value)}
                        className="h-7 text-xs border border-slate-200 rounded px-2 bg-white text-slate-700 min-w-[130px]"
                        data-testid={`alloc-select-${ci}`}
                      >
                        <option value="">Unallocated</option>
                        {waves.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                      </select>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        {waves.map((w, wi) => (
                          <label key={w.id} className="flex items-center gap-1 text-[10px] text-slate-500">
                            <span className="w-2 h-2 rounded-full" style={{ background: WAVE_COLORS[wi % WAVE_COLORS.length] }} />
                            {w.name}
                            <Input type="number" min="0" max="100"
                              value={fm[w.id] != null ? Math.round(fm[w.id] * 1000) / 10 : ''}
                              onChange={(e) => setFraction(comp.name, w.id, e.target.value)}
                              className="h-7 w-16 text-xs" data-testid={`alloc-pct-${ci}-${wi}`} />%
                          </label>
                        ))}
                        <span className={`text-[10px] font-semibold ${over ? 'text-rose-600' : totalPct === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-testid={`alloc-total-${ci}`}>
                          {totalPct}%
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setSplitMode(prev => ({ ...prev, [comp.name]: !isSplit }))}
                      className={`text-[10px] flex items-center gap-1 px-1.5 py-1 rounded transition-colors ${isSplit ? 'bg-[#4ABFCF]/15 text-[#0F6B78] font-semibold' : 'text-slate-400 hover:text-[#4ABFCF]'}`}
                      data-testid={`alloc-split-toggle-${ci}`}
                    >
                      <SplitSquareHorizontal className="w-3 h-3" /> Split
                    </button>
                  </div>
                );
              })}
            </div>
            {wavesMeta && wavesMeta.unallocated?.length > 0 && (
              <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-800" data-testid="waves-unallocated-warning">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  <strong>{wavesMeta.unallocated_hours}h{wavesMeta.unallocated_cost > 0 ? ` (${fmtCost(wavesMeta.unallocated_cost, currencySymbol)})` : ''} unallocated</strong> across {wavesMeta.unallocated.length} component{wavesMeta.unallocated.length === 1 ? '' : 's'} — this effort is not assigned to any wave: {wavesMeta.unallocated.slice(0, 4).map(u => `${u.name} (${u.remaining_hours}h)`).join(', ')}{wavesMeta.unallocated.length > 4 ? '…' : ''}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Per-wave summary cards (from computed wave_schedule) ── */}
      {cfg.enabled && waveSchedule.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2" data-testid="waves-summary-grid">
          {waveSchedule.map((ws, i) => (
            <Card key={ws.wave_id || i} data-testid={`wave-summary-card-${i}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: WAVE_COLORS[i % WAVE_COLORS.length] }} />
                  {ws.wave}
                  <Badge variant="secondary" className="text-[10px]">{ws.total_effort}h</Badge>
                  {ws.status && STATUS_META[ws.status] && (
                    <Badge className={`text-[10px] border ${STATUS_META[ws.status].cls}`} data-testid={`wave-status-badge-${i}`}>
                      {STATUS_META[ws.status].label}
                    </Badge>
                  )}
                  {ws.status === 'launched' && ws.actual_golive_date && (
                    <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-200" data-testid={`wave-actual-date-badge-${i}`}>
                      Live {fmtDate(ws.actual_golive_date)}
                    </Badge>
                  )}
                  {(() => { const sm = slipMeta(ws.slip_days); return sm ? (
                    <Badge className={`text-[10px] border ${sm.cls}`} data-testid={`wave-slip-badge-${i}`}>{sm.label}</Badge>
                  ) : null; })()}
                  {ws.total_cost > 0 && (
                    <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid={`wave-cost-badge-${i}`}>
                      {fmtCost(ws.total_cost, currencySymbol)}
                    </Badge>
                  )}
                  {ws.overrun_weeks > 0 && (
                    <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-300" data-testid={`wave-overrun-badge-${i}`}>
                      +{ws.overrun_weeks}w past plan end
                    </Badge>
                  )}
                </CardTitle>
                {ws.description && <p className="text-[11px] text-slate-500">{ws.description}</p>}
              </CardHeader>
              <CardContent className="pt-0 space-y-2.5">
                <div className="flex items-center gap-1.5 text-xs text-slate-600">
                  <CalendarRange className="w-3.5 h-3.5 text-[#4ABFCF]" />
                  W{ws.start_week}–W{ws.end_week} ({ws.duration_weeks} wks)
                  {ws.start_date && (
                    <span className="text-slate-400">· {fmtDate(ws.start_date)} → {fmtDate(ws.end_date)}</span>
                  )}
                </div>
                {ws.roles?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1">Seertech roles</div>
                    <div className="flex flex-wrap gap-1.5">
                      {ws.roles.map(r => (
                        <Badge key={r.role} variant="outline" className="text-[10px]">{r.role_label}: {r.effort}h</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {ws.client_resources?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1 flex items-center gap-1">
                      <Users className="w-3 h-3" /> Client resources
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {ws.client_resources.map(cr => (
                        <Badge key={cr.role} variant="outline" className="text-[10px] border-slate-200 text-slate-600">
                          {cr.label}: {cr.total_hours}h ({cr.avg_commitment}% avg)
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div className="text-[10px] text-slate-400">
                  {(ws.components || []).filter(c => !c.is_stage).length} scope item{(ws.components || []).filter(c => !c.is_stage).length === 1 ? '' : 's'}
                  {(ws.components || []).some(c => c.is_stage) && ' · own lifecycle'}
                </div>
                {(ws.timing_warnings || []).length > 0 && (
                  <div className="space-y-1" data-testid={`wave-timing-warnings-${i}`}>
                    {ws.timing_warnings.map((tw, ti) => (
                      <div key={ti} className="flex items-start gap-1.5 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`timing-warning-${i}-${ti}`}>
                        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                        <span>{tw.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Payment Schedule ── */}
      {cfg.enabled && wavesMeta?.payment_schedule?.length > 0 && (
        <Card data-testid="wave-payment-schedule-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#0F2040] flex items-center gap-2">
              <Receipt className="w-4 h-4 text-[#4ABFCF]" /> Payment Schedule
            </CardTitle>
            <p className="text-[11px] text-slate-500">Client-facing schedule — each wave is invoiced at its go-live milestone. Included in the Design Pack and exports.</p>
          </CardHeader>
          <CardContent className="pt-0">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] uppercase tracking-wide text-slate-400 border-b border-slate-200">
                  <th className="text-left py-1.5 font-semibold">Invoice trigger</th>
                  <th className="text-left py-1.5 font-semibold">Timing</th>
                  <th className="text-right py-1.5 font-semibold">Amount</th>
                  <th className="text-right py-1.5 font-semibold">% of schedule</th>
                  <th className="text-right py-1.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {wavesMeta.payment_schedule.map((r, i) => (
                  <tr key={r.wave_id || `pay-${i}`} className="border-b border-slate-100" data-testid={`payment-row-${i}`}>
                    <td className="py-1.5 font-medium text-[#0F2040]">{r.trigger}</td>
                    <td className="py-1.5 text-slate-500">{r.date ? fmtDate(r.date) : r.week ? `Week ${r.week}` : '—'}</td>
                    <td className="py-1.5 text-right font-semibold text-[#0F2040]">{fmtCost(r.amount, currencySymbol)}</td>
                    <td className="py-1.5 text-right text-slate-500">{r.pct_of_schedule}%</td>
                    <td className="py-1.5 text-right">
                      {r.status && STATUS_META[r.status] ? (
                        <Badge className={`text-[9px] border ${STATUS_META[r.status].cls}`}>{STATUS_META[r.status].label}</Badge>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
                <tr data-testid="payment-total-row">
                  <td className="py-2 font-bold text-[#0F2040]">Total</td>
                  <td />
                  <td className="py-2 text-right font-bold text-[#0F2040]">
                    {fmtCost(wavesMeta.payment_schedule.reduce((s, r) => s + (r.amount || 0), 0), currencySymbol)}
                  </td>
                  <td className="py-2 text-right font-bold text-[#0F2040]">100%</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── Compare layouts dialog ── */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="wave-compare-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F2040] flex items-center gap-2">
              <Scale className="w-4 h-4 text-[#4ABFCF]" /> Compare wave layouts
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-slate-500">Third layout:</span>
            <select
              value={compareTplC?.id || ''}
              onChange={(e) => {
                const tpl = templates.find(t => t.id === e.target.value) || null;
                setCompareTplC(tpl);
                if (compareTplB) runCompare(compareTplB, tpl);
              }}
              className="h-7 text-xs border border-slate-200 rounded px-2 bg-white text-slate-700 min-w-[160px]"
              data-testid="wave-compare-third-select"
            >
              <option value="">None</option>
              {templates.filter(t => t.id !== compareTplB?.id).map(t => (
                <option key={t.id} value={t.id}>{t.name} ({(t.waves || []).length} waves)</option>
              ))}
            </select>
            {templates.filter(t => t.id !== compareTplB?.id).length === 0 && (
              <span className="text-[10px] text-slate-400" data-testid="wave-compare-third-hint">save another template to compare three layouts</span>
            )}
            {comparing && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
          </div>
          {compareData && (
            <div className={`grid gap-4 ${compareData.c ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
              {['a', 'b', 'c'].map((side) => {
                const layout = compareData[side];
                if (!layout) return null;
                const meta = layout.meta || {};
                const sideTpl = side === 'b' ? compareTplB : side === 'c' ? compareTplC : null;
                return (
                  <div key={side} className={`border rounded-md p-3 ${side !== 'a' ? 'border-[#4ABFCF]/40 bg-[#4ABFCF]/[0.03]' : 'border-slate-200'}`} data-testid={`compare-layout-${side}`}>
                    <div className="text-sm font-semibold text-[#0F2040] mb-2 flex items-center justify-between gap-2">
                      <span className="truncate">{layout.label}</span>
                      {sideTpl && (
                        <Button size="sm" variant="outline" className="h-6 text-[10px] shrink-0"
                          onClick={() => { applyTemplateObj(sideTpl); setCompareOpen(false); }}
                          data-testid={`wave-compare-apply-${side}-btn`}>
                          Apply
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {(layout.schedule || []).map((ws, i) => (
                        <div key={ws.wave_id || i} className="border border-slate-100 rounded p-2 bg-white">
                          <div className="text-xs font-medium text-[#0F2040] flex items-center gap-1.5 flex-wrap">
                            <span className="w-2 h-2 rounded-full" style={{ background: WAVE_COLORS[i % WAVE_COLORS.length] }} />
                            {ws.wave}
                            <span className="text-[10px] text-slate-400">W{ws.start_week}–W{ws.end_week}</span>
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {ws.total_effort}h{ws.total_cost > 0 ? ` · ${fmtCost(ws.total_cost, currencySymbol)}` : ''} · {(ws.components || []).filter(c => !c.is_stage).length} items
                            {ws.overrun_weeks > 0 && <span className="text-amber-600 font-semibold"> · +{ws.overrun_weeks}w overrun</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-500 space-y-0.5">
                      <div>Allocated: <strong className="text-[#0F2040]">{meta.total_allocated_hours}h{meta.total_allocated_cost > 0 ? ` (${fmtCost(meta.total_allocated_cost)})` : ''}</strong></div>
                      <div>Unallocated: <strong className={meta.unallocated_hours > 0 ? 'text-amber-600' : 'text-emerald-600'}>{meta.unallocated_hours}h{meta.unallocated_cost > 0 ? ` (${fmtCost(meta.unallocated_cost)})` : ''}</strong></div>
                      {(meta.overruns || []).length > 0 && (
                        <div className="text-amber-600">Overruns: {meta.overruns.map(o => `${o.name} +${o.overrun_weeks}w`).join(', ')}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setCompareOpen(false)} data-testid="wave-compare-close-btn">Close</Button>
            <Button size="sm" className="bg-[#0F2040] hover:bg-[#1a3057] text-white"
              onClick={() => { applyTemplateObj(compareTplB); setCompareOpen(false); }}
              data-testid="wave-compare-apply-btn">
              Apply {compareData?.b?.label || 'alternative'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* ── v261 — Mirror preview: before/after strip of wave positions ── */}
      <Dialog open={!!mirrorPreview} onOpenChange={(o) => { if (!o) setMirrorPreview(null); }}>
        <DialogContent className="max-w-xl" data-testid="wave-mirror-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F2040] flex items-center gap-2 text-base">
              <CopyPlus className="w-4 h-4 text-[#4ABFCF]" />
              Mirror {mirrorPreview?.src?.name} — preview
            </DialogTitle>
          </DialogHeader>
          {mirrorPreview && (() => {
            const all = [...mirrorPreview.before, ...mirrorPreview.after].filter(w => w.start_week && w.duration_weeks);
            const span = Math.max(1, ...all.map(w => w.start_week + w.duration_weeks - 1));
            return (
              <div className="space-y-4">
                <WaveStrip list={mirrorPreview.before} span={span} label="Before" templateId={mirrorPreview.src.id} testId="wave-mirror-preview-before" />
                <WaveStrip list={mirrorPreview.after} span={span} label="After" templateId={mirrorPreview.src.id} testId="wave-mirror-preview-after" />
                <div className="flex justify-between text-[9px] text-slate-400 pl-[104px]">
                  <span>W1</span>
                  <span>W{span}</span>
                </div>
                <div data-testid="wave-mirror-targets">
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Apply to</div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {mirrorPreview.candidates.map((w, ci) => (
                      <label key={w.id} className="inline-flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={mirrorTargets.has(w.id)}
                          onChange={() => toggleMirrorTarget(w.id)}
                          className="w-3.5 h-3.5 accent-[#0F2040]"
                          data-testid={`wave-mirror-target-${ci}`}
                        />
                        {w.name}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded px-2.5 py-1.5" data-testid="wave-mirror-preview-summary">
                  {mirrorPreview.others.length === 0
                    ? 'No waves selected — tick at least one wave to apply the template to.'
                    : (mirrorPreview.bits.length
                      ? mirrorPreview.bits.join(' · ')
                      : `No wave-level changes — give ${mirrorPreview.src.name} an explicit Start + Weeks to chain the selected waves around it.`)}
                  {mirrorPreview.others.length > 0 && ' Nothing is saved until you click Save waves.'}
                </div>
              </div>
            );
          })()}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => setMirrorPreview(null)} data-testid="wave-mirror-cancel-btn">Cancel</Button>
            <Button size="sm" className="bg-[#0F2040] hover:bg-[#1a3057] text-white" onClick={applyMirror}
              disabled={!mirrorPreview || mirrorPreview.others.length === 0} data-testid="wave-mirror-confirm-btn">
              Apply mirror
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default WavesPanel;
